import React, { ReactNode } from "react";
import { Button, Grid } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import { FileWithPath } from "react-dropzone";

import { APIError } from "src/types";
import { Dropzone } from "src/components/Dropzone";
import { DropzoneHeader } from "src/components/DropzoneHeader";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { usePoiImportSchema, SUPPORTED_FORMATS } from "src/schemas/poi-import.schema";
import { fieldToError } from "src/utils/form.utils";
import { DropzoneSingleFileRequirementError } from "src/errors";
import { useTemplates } from "src/hooks";

export type PoiImportFormData = {
  poiFile: File | null;
};

interface Props {
  data: PoiImportFormData;
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (values: PoiImportFormData) => void;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<PoiImportFormData> | null>;
  statusBody: ReactNode;
}

export function PoiImportForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = usePoiImportSchema();
  const { data: templates } = useTemplates();

  const initialValues: PoiImportFormData = {
    poiFile: props.data.poiFile,
  };

  return render();

  function render() {
    if (!templates) return null;

    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          return props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ isSubmitting, isValid, dirty, setFieldValue, errors }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <DropzoneHeader label={t("label.poiFile")} template={templates.poiTemplateUrl} />
                <Dropzone
                  dropzoneOptions={{
                    onDrop: (acceptedFiles: FileWithPath[]) => {
                      if (acceptedFiles.length > 1) throw DropzoneSingleFileRequirementError();

                      setFieldValue("poiFile", acceptedFiles[0]);
                    },
                    accept: SUPPORTED_FORMATS,
                    multiple: false,
                  }}
                />

                {errors.poiFile && (
                  <Grid item xs={12}>
                    <ServerErrors data={[fieldToError("pioFileError", errors.poiFile)]} />
                  </Grid>
                )}
              </Grid>

              {props.statusBody}

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              {!props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.close")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!(dirty && isValid)}
                  >
                    {t("label.upload")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
